import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardEventHandler } from 'react';
import { Flex, useBreakpointValue } from '@chakra-ui/react';
import debounce from 'lodash-es/debounce';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import type { RadarAutocompleteAddress, RadarAutocompleteResponse } from 'radar-sdk-js/dist/types';
import { ShippingMethodId } from '../../bff-api/baskets/model/ShippingMethodId';
import { useStoreSearch } from '../../bff-api/stores/useStoreSearch';
import { StorageKey } from '../../hooks/model/StorageKey';
import { useLocalStorageMutation } from '../../hooks/useLocalStorage';
import { Text } from '../../ui-components';
import { Drawer } from '../../ui-components/drawer';
import { toOrderAddress } from '../../utils/address-utils';
import { useCustomPreferenceOrDefault } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { radarAutocomplete } from '../../utils/radar/radarAutocomplete';
import { Field } from '../field';
import { ADDRESS_FIELD, useAddressAutocompleteField } from '../forms/useAddressAutocompleteField';
import type { OrderAddress } from '../../bff-api/baskets/model';
import type { Store } from '../../bff-api/stores/model/Store';

interface Props {
    id: string;
    productId?: string;
    isOpen: boolean;
    isLoading: boolean;
    onClose: () => void;
    onChangeAddress?: (address: OrderAddress, store: Store | undefined) => Promise<void> | void;
    closeOnSuccess?: boolean;
}

export const SDDStoreSearchDrawer: React.FC<Props> = ({
    id,
    productId,
    isOpen,
    isLoading,
    onClose,
    onChangeAddress,
    closeOnSuccess = false,
}: Props) => {
    const [radarAddress, setRadarAddress] = useState<RadarAutocompleteAddress | undefined>();
    const addressSuggestDebounce = useCustomPreferenceOrDefault(
        CustomPreference.ADDRESS_SUGGEST_DEBOUNCE,
        300,
    );
    const intl = useIntl();
    const form = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: { [ADDRESS_FIELD]: '' },
    });
    const [addresses, setAddresses] = useState<RadarAutocompleteAddress[]>([]);
    const modalBodyRef = useRef<HTMLDivElement>(null);
    const addressField = useAddressAutocompleteField(form);
    const updateSDDAddress = useLocalStorageMutation(StorageKey.SDD_ADDRESS);
    const updateSDDStore = useLocalStorageMutation(StorageKey.SDD_STORE);
    const storeSearchInventory = useStoreSearch();

    useEffect(() => {
        if (!isLoading && !updateSDDStore.isLoading && !storeSearchInventory.isLoading) {
            onClose();
        }
    }, [isLoading, onClose, updateSDDStore.isLoading, storeSearchInventory.isLoading]);

    const onChange = useMemo(
        () =>
            debounce(async (value: string): Promise<void> => {
                if (radarAddress && radarAddress.addressLabel === value) {
                    return;
                }

                if (value.length > 2) {
                    const response: RadarAutocompleteResponse = await radarAutocomplete(value);
                    setAddresses(response.addresses);
                } else {
                    setAddresses([]);
                }
            }, addressSuggestDebounce),
        [addressSuggestDebounce, radarAddress],
    );
    const onClick = async (): Promise<void> => {
        const address = toOrderAddress(radarAddress!);
        await updateSDDAddress.mutateAsync(address);

        setAddresses([]);

        const { postalCode } = address;

        if (postalCode) {
            const stores = await storeSearchInventory.mutateAsync({
                postalCode,
                productId,
                shippingMethodId: ShippingMethodId.SAME_DAY_DELIVERY,
            });

            const store = stores.length ? stores[0] : undefined;

            await updateSDDStore.mutateAsync(store);
            await onChangeAddress?.(address, store);
        }

        if (closeOnSuccess && !isLoading) {
            onClose();
        }
    };
    const onKeyDown: KeyboardEventHandler<HTMLInputElement> = async (e) => {
        if (e.key === 'Enter' && radarAddress && !updateSDDAddress.isLoading) {
            await onClick();
        }
    };
    const autocompleteInputProps = {
        onChange,
        onKeyDown,
        handleAutocompleteSelect: (selectedAddress: RadarAutocompleteAddress): void => {
            setAddresses([]);
            setRadarAddress(selectedAddress);
        },
    };

    const handleViewPortOrItemsChange = useCallback((): void => {
        const bodyStart = modalBodyRef.current?.getBoundingClientRect().top;
        if (window.visualViewport && bodyStart) {
            setResultMaxHeight(`${window.visualViewport.height - bodyStart}px`);
        }
    }, []);

    const [resultMaxHeight, setResultMaxHeight] = useState('100dvh');

    useEffect(() => {
        window.visualViewport?.addEventListener('resize', handleViewPortOrItemsChange);

        return () => {
            window.visualViewport?.removeEventListener('resize', handleViewPortOrItemsChange);
        };
    }, [handleViewPortOrItemsChange]);

    const isFullPageDrawer = useBreakpointValue({
        base: true,
        md: false,
    });

    return (
        <Drawer
            id={id}
            title={intl.formatMessage({
                id: 'location.search.title',
                defaultMessage: 'Enter your Location',
            })}
            isOpen={isOpen}
            onClose={onClose}
            callToAction={{
                children: intl.formatMessage({
                    id: 'location.search.search',
                    defaultMessage: 'Search',
                }),
                onClick,
                isDisabled: !radarAddress,
                isLoading:
                    updateSDDAddress.isLoading ||
                    updateSDDStore.isLoading ||
                    storeSearchInventory.isLoading ||
                    isLoading,
            }}
            isFullPage={isFullPageDrawer}
            containerStyle={{ p: 0, paddingLeft: 4, paddingRight: 4 }}
        >
            <Flex maxHeight={resultMaxHeight} overflowY="auto" ref={modalBodyRef}>
                <Flex direction="column" rowGap={4} flex="1">
                    <Text id={`${id}-description`} color="slt-pepper.1000">
                        <FormattedMessage
                            id="location.search.description"
                            defaultMessage="Enter your location to see availability for Same-Day Delivery"
                        />
                    </Text>

                    <Field
                        {...addressField}
                        inputProps={autocompleteInputProps}
                        comboboxOptions={addresses}
                    />
                </Flex>
            </Flex>
        </Drawer>
    );
};

SDDStoreSearchDrawer.defaultProps = {
    productId: undefined,
    onChangeAddress: undefined,
    closeOnSuccess: false,
};
